import { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { useClients } from '@/hooks/useClients';
import { useSubscription } from '@/hooks/useSubscription';
import ClientManagement from './ClientManagement';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';

interface ClientSelectorProps {
  selectedClientId?: string;
  onClientChange: (clientId: string | undefined) => void;
  label?: string;
  placeholder?: string;
}

const ClientSelector = ({ 
  selectedClientId, 
  onClientChange, 
  label = "Client",
  placeholder = "Select a client (optional)"
}: ClientSelectorProps) => {
  const { clients, loading } = useClients();
  const { hasClientPortalAccess } = useSubscription();
  const [showClientDialog, setShowClientDialog] = useState(false);

  // Don't render for non-pro/enterprise users
  if (!hasClientPortalAccess()) {
    return null;
  }

  const handleValueChange = (value: string) => {
    if (value === 'none') {
      onClientChange(undefined);
    } else {
      onClientChange(value);
    }
  };

  if (loading) {
    return (
      <div>
        <Label>{label}</Label>
        <div className="text-sm text-muted-foreground">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Select
          value={selectedClientId || 'none'}
          onValueChange={handleValueChange}
        >
          <SelectTrigger className="flex-1 bg-background border">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent className="bg-background border shadow-lg z-50">
            <SelectItem value="none">No client (personal event)</SelectItem>
            {clients.map((client) => (
              <SelectItem key={client.id} value={client.id}>
                {client.name} {client.company && `(${client.company})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
          <DialogTrigger asChild>
            <Button variant="outline" size="icon">
              <Plus className="h-4 w-4" />
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-background border">
            <ClientManagement />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ClientSelector;